/**
 * Email thread API hook (Phase 3 第3層).
 * GET /api/emails/:provider_thread_id/thread
 */
import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

export type ThreadMessageItem = {
  direction: "sent" | "received";
  occurredAt: string;
  subject: string;
  fromEmail: string;
  fromName: string | null;
  toEmails: string[];
  bodyText: string | null;
  bodyHtml: string | null;
  messageId: string | null;
  inReplyTo: string | null;
  sentEmailId: string | null;
  replyEmailId: string | null;
};

export type EmailThreadResponse = {
  providerThreadId: string;
  subject: string;
  items: ThreadMessageItem[];
};

/**
 * provider_thread_id でスレッド全体を取得する hook
 * @param {object} options options
 * @param {string|null} options.providerThreadId スレッド ID (null なら fetch しない)
 * @return {object} thread, loading, error, refetch
 */
export function useEmailThread(options: { providerThreadId: string | null }): {
  thread: EmailThreadResponse | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const { providerThreadId } = options;
  const [thread, setThread] = useState<EmailThreadResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchThread = useCallback(async () => {
    if (!providerThreadId) {
      setThread(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.get<EmailThreadResponse>(
        `/emails/${encodeURIComponent(providerThreadId)}/thread`,
      );
      setThread(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [providerThreadId]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  return { thread, loading, error, refetch: fetchThread };
}
